/* eslint-disable indent */
import { BinIcon, GreenTickIcon, PdfGreyIcon, PrinterIcon, RefundedBlueIcon } from 'assets/icons';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { VoidFn } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsInvoices } from '../events';

import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { PaymentCategoryTypes } from 'containers/settings/type';
import { StatusIndicator } from '../constants';

interface MoreMenuProps {
  statusIndicator: StatusIndicator;
  hasRefundableTransaction: boolean;
  onExportPdf: VoidFn;
  onPrintPdf: VoidFn;
  isModifiable: boolean;
}

const MoreMenu: FC<MoreMenuProps> = props => {
  const { statusIndicator, onExportPdf, onPrintPdf, isModifiable, hasRefundableTransaction } = props;

  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { data: merchantCustomisations } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });

  const { paymentModes } = merchantCustomisations || {};

  const isMarkAsPaidEnabled = paymentModes?.some(
    item => item.category.type === PaymentCategoryTypes.MANUAL && item.enabled
  );

  const {
    rootInvoiceId,
    invoiceId: invoiceIdFromUrl,
    rootCustomerId,
    rootPage,
    rootReceiptId,
    rootJobId,
    rootEstimateId
  } = useParams();

  const handleRecordPaymentRoute = () => {
    if (rootInvoiceId) {
      if (invoiceIdFromUrl) {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/record-payment/${invoiceIdFromUrl}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/record-payment`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/record-payment/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/record-payment/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/record-payment/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/record-payment/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/record-payment/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleDeleteInvoice = () => {
    if (rootInvoiceId) {
      if (invoiceIdFromUrl) {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/delete-invoice/${invoiceIdFromUrl}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/delete-invoice`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/delete-invoice/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/delete-invoice/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/delete-invoice/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/delete-invoice/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/delete-invoice/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleIssueRefund = () => {
    if (rootInvoiceId) {
      if (invoiceIdFromUrl) {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/issue-refund/${invoiceIdFromUrl}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/issue-refund`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/issue-refund/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/issue-refund/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/issue-refund/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/issue-refund/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/issue-refund/${invoiceIdFromUrl}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const menuItems = [
    ...(statusIndicator !== StatusIndicator.FULLY_PAID && isMarkAsPaidEnabled && isModifiable
      ? [
          {
            id: 'm_1',
            icon: <GreenTickIcon className="w-5 shrink-0" />,
            text: 'Record a payment',
            onClick: () => {
              logAnalyticEvent(CleverTapEventsInvoices.webInvoicesListDetailMoreMarkAsPaid);
              handleRecordPaymentRoute();
            }
          }
        ]
      : []),
    ...(hasRefundableTransaction
      ? [
          {
            id: 'm_5',
            icon: <RefundedBlueIcon className="w-5 shrink-0" />,
            text: 'Issue refund',
            onClick: handleIssueRefund
          }
        ]
      : []),
    {
      id: 'm_2',
      icon: <PdfGreyIcon className="w-5 shrink-0" />,
      text: 'Export PDF',
      onClick: onExportPdf
    },
    {
      id: 'm_3',
      icon: <PrinterIcon className="w-5 shrink-0" />,
      text: 'Print',
      onClick: onPrintPdf
    },
    ...(statusIndicator === StatusIndicator.NO_PAYMENTS_YET
      ? [
          {
            id: 'm_4',
            icon: <BinIcon className="w-5 shrink-0" />,
            text: 'Delete',
            onClick: () => {
              logAnalyticEvent(CleverTapEventsInvoices.webInvoicesListDetailMoreDelete);
              handleDeleteInvoice();
            }
          }
        ]
      : [])
  ];

  const isActionAllowed = (actionId: string) => {
    return isModifiable || actionId === 'm_2' || actionId === 'm_3' || actionId === 'm_5';
  };
  return (
    <div className="flex flex-col space-y-4 whitespace-nowrap bg-primary text-sbase font-semibold text-primaryText">
      {menuItems.map(menu => (
        <div
          onClick={menu.onClick}
          className={`flex flex-row items-center gap-2 ${
            isActionAllowed(menu.id) ? '' : 'pointer-events-none cursor-not-allowed opacity-50'
          }`}
          key={menu.id}>
          {menu.icon}
          <div>{menu.text}</div>
        </div>
      ))}
    </div>
  );
};

export default MoreMenu;
